import { Button, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import { platformUsersKeys } from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";
import { useFileNaming } from "../../../../hooks/useFileNaming";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons } from "../../../stack/environment/icons/iconExport";

function ExportNistPdfButton({ containerRef, disabled = false }) {
  const [isExporting, setIsExporting] = useState(false);
  const { trackEvent, getPlatformUser } = useAPI();
  const colors = useColors();
  const { isMSSP, currentTenantId } = useMSSP();
  const { generatePdfFileName } = useFileNaming();
  // Get platform user data to access customer_id for non-MSSP users
  const { data: platformUser } = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  // Determine the customer ID based on user type
  const getCustomerId = () => {
    if (isMSSP) {
      return currentTenantId;
    }
    return platformUser?.customer_id;
  };

  async function handleExport() {
    trackEvent("User exported NIST view to PDF");
    if (!containerRef.current) {
      return;
    }

    setIsExporting(true);
    try {
      const container = containerRef.current;
      const customerId = getCustomerId();
      const currentDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const clone = container.cloneNode(true);
      if (!(clone instanceof HTMLElement))
        throw new Error("Failed to clone the container");

      // Create a wrapper div that will contain the header and the cloned content
      const wrapper = document.createElement("div");
      wrapper.style.position = "absolute";
      wrapper.style.left = "-9999px";
      wrapper.style.top = "-9999px";
      wrapper.style.overflow = "visible";
      wrapper.style.backgroundColor = "#ffffff";
      wrapper.style.padding = "20px";
      wrapper.style.fontFamily = "Arial, sans-serif";

      // Create and add header with date and customer ID
      const header = document.createElement("div");
      header.style.marginBottom = "20px";
      header.style.borderBottom = "2px solid #e0e0e0";
      header.style.paddingBottom = "10px";

      const title = document.createElement("h1");
      title.textContent = "NIST Cybersecurity Framework View";
      title.style.margin = "0 0 10px 0";
      title.style.fontSize = "24px";
      title.style.fontWeight = "bold";
      title.style.color = "#333";

      const dateInfo = document.createElement("p");
      dateInfo.textContent = `Generated on: ${currentDate}`;
      dateInfo.style.margin = "5px 0";
      dateInfo.style.fontSize = "14px";
      dateInfo.style.color = "#666";

      const customerInfo = document.createElement("p");
      customerInfo.textContent = `Customer: ${customerId || "N/A"}`;
      customerInfo.style.margin = "5px 0";
      customerInfo.style.fontSize = "14px";
      customerInfo.style.color = "#666";

      header.appendChild(title);
      header.appendChild(dateInfo);
      header.appendChild(customerInfo);

      // Prepare the clone for export
      clone.style.position = "relative";
      clone.style.overflow = "visible";
      clone.style.maxHeight = "none";
      clone.style.height = "auto";
      clone.style.width = container.scrollWidth + "px";
      clone.style.whiteSpace = "nowrap";

      // Add header and content to wrapper
      wrapper.appendChild(header);
      wrapper.appendChild(clone);

      // Optimize for single-page export
      const allElements = clone.querySelectorAll("*");
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          // Remove visual styling that takes up space
          el.style.overflow = "visible";
          el.style.maxHeight = "none";
          el.style.boxShadow = "none";
          el.style.margin = el.style.margin || "0";

          // Reduce padding to compress content
          if (el.style.padding) {
            const currentPadding = parseInt(el.style.padding);
            if (!isNaN(currentPadding) && currentPadding > 8) {
              el.style.padding = Math.max(4, currentPadding / 2) + "px";
            }
          }

          // Reduce font size for better fit
          if (el.style.fontSize) {
            const currentSize = parseInt(el.style.fontSize);
            if (!isNaN(currentSize) && currentSize > 12) {
              el.style.fontSize = Math.max(10, currentSize * 0.85) + "px";
            }
          }
        }
      });

      // Find and hide the "View Function Details" elements
      const eastIcons = clone.querySelectorAll(
        'svg[data-testid="EastRoundedIcon"]'
      );
      eastIcons.forEach((icon) => {
        // Find parent elements that contain both the icon and the text
        let parent = icon.parentElement;
        while (parent && parent !== clone) {
          if (
            parent.textContent &&
            parent.textContent.includes("View Function Details")
          ) {
            if (parent instanceof HTMLElement) {
              parent.style.display = "none";
            }
            break;
          }
          parent = parent.parentElement;
        }
      });

      document.body.appendChild(wrapper);

      // Get dimensions for the canvas
      const actualWidth = wrapper.scrollWidth;
      const actualHeight = wrapper.scrollHeight;
      wrapper.style.width = actualWidth + "px";
      wrapper.style.height = actualHeight + "px";

      // Create the canvas with adjusted scale for better fit
      const canvas = await html2canvas(wrapper, {
        scale: 1.2, // Reduced scale to fit more content
        useCORS: true,
        allowTaint: true,
        backgroundColor: colors.common_white,
        width: actualWidth,
        height: actualHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: actualWidth,
        windowHeight: actualHeight,
        logging: false,
        imageTimeout: 0,
      });

      document.body.removeChild(wrapper);

      // Create PDF with optimized settings
      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const pdf = new jsPDF({
        orientation: "landscape", // Landscape mode fits wider content
        unit: "mm",
        format: "a3", // Use larger A3 format for more content
        compress: true,
      });

      // Calculate dimensions to fit content on one page
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate image dimensions to fit on one page
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      // If image height exceeds page height, scale down proportionally
      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      // Center the image on the page
      const xOffset = (pdfWidth - imgWidth) / 2;
      const yOffset = (pdfHeight - imgHeight) / 2;

      // Add the image to the PDF
      pdf.addImage(
        imgData,
        "JPEG",
        xOffset,
        yOffset,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      );

      pdf.save(generatePdfFileName("nist_csf"));
    } catch (error) {
      Toast.error("Failed to export NIST CSF, Please try again later");
      trackEvent("Failed to export NIST CSF", { error: error.message });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Button
      startIcon={
        <SvgToIcon
          IconLogo={icons.ExportPDFIcon}
          style={{ width: "18px", color: "transparent" }}
        />
      }
      onClick={handleExport}
      disabled={isExporting || disabled}
      sx={{
        height: "35px",
        border: "1px solid " + colors.grey_32percent,
        textTransform: "unset",
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: colors.text_secondary,
        }}
      >
        {isExporting ? "Exporting..." : "Export to PDF"}
      </Typography>
    </Button>
  );
}

export default ExportNistPdfButton;
